// TurmasView — CRUD de turmas do professor + sincronização de bancos

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Database,
  GraduationCap,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  Users,
  UsersRound,
  X
} from "lucide-react";
import { api, ApiError, type ClassDetail, type ClassRow } from "../api";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const classes = signal<ClassRow[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const editing = signal<ClassDetail | null>(null);
const creating = signal<boolean>(false);
const deleting = signal<ClassRow | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listClasses();
    classes.value = r.classes;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

async function openEdit(id: string) {
  try {
    const r = await api.getClass(id);
    editing.value = r.class;
  } catch (e) {
    void openAlert({ title: "Erro", message: friendlyError(e) });
  }
}

export function TurmasView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading") {
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }
  const rows = classes.value ?? [];
  const q = search.value.toLowerCase().trim();
  const visible = useMemo(() => {
    return rows.filter((c) => {
      if (q) {
        const hay = `${c.name} ${c.description ?? ""} ${c.tags.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q]);

  return (
    <div class="space-y-4">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <UsersRound className="size-6 text-primary" />
            Turmas
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Cada turma agrupa alunos e bancos. Use "Sincronizar acesso" pra liberar os
            bancos automaticamente.
          </p>
        </div>
        <button
          type="button"
          onClick={() => (creating.value = true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" />
          Nova turma
        </button>
      </header>

      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            placeholder="Buscar nome, descrição, tag…"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhuma turma encontrada.
        </div>
      ) : (
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((c) => (
            <ClassCard key={c.id} group={c} />
          ))}
        </div>
      )}

      {editing.value && <EditModal group={editing.value} />}
      {creating.value && <CreateModal />}
      {deleting.value && <DeleteModal group={deleting.value} />}
    </div>
  );
}

function ClassCard({ group }: { group: ClassRow }) {
  return (
    <div class="space-y-2 rounded-md border border-border bg-card/40 p-4">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0 flex-1">
          <h3 class="truncate text-sm font-bold">{group.name}</h3>
          {group.description && (
            <p class="line-clamp-2 text-[11px] text-muted-foreground">{group.description}</p>
          )}
        </div>
        <div class="inline-flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => openEdit(group.id)}
            title="Editar"
            aria-label="Editar"
            class="rounded-md border border-border bg-background p-1.5 text-xs hover:bg-accent"
          >
            <Pencil width="0.9em" height="0.9em" />
          </button>
          <button
            type="button"
            onClick={() => (deleting.value = group)}
            title="Excluir"
            aria-label="Excluir"
            class="rounded-md border border-red-500/40 bg-red-500/5 p-1.5 text-xs text-red-600 hover:bg-red-500/10"
          >
            <Trash2 width="0.9em" height="0.9em" />
          </button>
        </div>
      </div>
      <div class="flex items-center gap-2 text-[11px]">
        <span class="inline-flex items-center gap-1 rounded bg-accent/60 px-1.5 py-0.5 font-bold tabular-nums">
          <Users width="0.9em" height="0.9em" />
          {group.memberCount}
        </span>
        <span class="inline-flex items-center gap-1 rounded bg-accent/60 px-1.5 py-0.5 font-bold tabular-nums">
          <Database width="0.9em" height="0.9em" />
          {group.bankCount}
        </span>
        <span class="text-[10px] text-muted-foreground">
          atualizada {new Date(group.updatedAt).toLocaleDateString("pt-BR")}
        </span>
      </div>
    </div>
  );
}

function CreateModal() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  async function go() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.createClass({
        name: name.trim(),
        description: description.trim() || null
      });
      creating.value = false;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal title="Nova turma" icon={UsersRound} onClose={() => (creating.value = false)}>
      <Field label="Nome *">
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          autoFocus
          aria-label="Nome da turma"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Descrição">
        <textarea
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
          rows={2}
          aria-label="Descrição"
          placeholder="Opcional"
          class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <p class="text-[11px] text-muted-foreground">
        Membros e bancos você adiciona depois pela edição.
      </p>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (creating.value = false)}
          disabled={saving}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={go}
          disabled={saving || !name.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Plus width="1em" height="1em" />}
          Criar
        </button>
      </ModalFooter>
    </Modal>
  );
}

function EditModal({ group }: { group: ClassDetail }) {
  const [detail, setDetail] = useState<ClassDetail>(group);
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? "");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    try {
      const r = await api.patchClass(group.id, {
        name: name.trim(),
        description: description.trim() || null
      });
      setDetail(r.class);
      editing.value = null;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }
  async function syncGrants() {
    setSyncing(true);
    setInfo(null);
    try {
      const r = await api.syncClassGrants(group.id);
      setInfo(
        `✓ ${r.granted} grant${r.granted !== 1 ? "s" : ""} criado${r.granted !== 1 ? "s" : ""} (${r.skipped} já existiam) — ${r.members} aluno${r.members !== 1 ? "s" : ""} × ${r.banks} banco${r.banks !== 1 ? "s" : ""}.`
      );
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSyncing(false);
    }
  }

  return (
    <Modal title={`Editar: ${detail.name}`} icon={UsersRound} onClose={() => (editing.value = null)}>
      <Field label="Nome">
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          aria-label="Nome"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Descrição">
        <textarea
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
          rows={2}
          aria-label="Descrição"
          class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>

      <div class="rounded-md border border-border bg-background/50 p-3">
        <h3 class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <Users width="1em" height="1em" />
          Alunos ({detail.members.length})
        </h3>
        {detail.members.length === 0 ? (
          <p class="text-[11px] italic text-muted-foreground">Vazio. Adicione pelo backend ou pelo manager.</p>
        ) : (
          <ul class="max-h-[180px] space-y-0.5 overflow-y-auto">
            {detail.members.map((m) => (
              <li key={m.id} class="flex items-center gap-2 rounded bg-background px-2 py-1 text-[11px]">
                <span class="min-w-0 flex-1 truncate font-semibold">{m.name}</span>
                <span class="truncate font-mono text-[10px] text-muted-foreground">{m.email}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-3">
        <div class="mb-2 flex items-center justify-between">
          <h3 class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
            <Database width="1em" height="1em" />
            Bancos ({detail.banks.length})
          </h3>
          <button
            type="button"
            onClick={syncGrants}
            disabled={syncing || detail.banks.length === 0 || detail.members.length === 0}
            class="inline-flex items-center gap-1 rounded-md border border-violet-500/40 bg-violet-500/15 px-2 py-1 text-[10px] font-bold text-violet-700 hover:bg-violet-500/25 disabled:opacity-40 dark:text-violet-300"
          >
            {syncing ? <Loader2 className="size-3 animate-spin" /> : <Link2 width="0.9em" height="0.9em" />}
            Sincronizar acesso
          </button>
        </div>
        {detail.banks.length === 0 ? (
          <p class="text-[11px] italic text-muted-foreground">Sem bancos atribuídos. Use o gerenciamento de bancos pra adicionar.</p>
        ) : (
          <ul class="space-y-0.5">
            {detail.banks.map((b) => (
              <li key={b.id} class="rounded bg-background px-2 py-1 text-[11px] font-semibold">
                {b.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      {info && (
        <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
          {info}
        </div>
      )}

      <ModalFooter>
        <button
          type="button"
          onClick={() => (editing.value = null)}
          disabled={saving}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Fechar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !name.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save width="1em" height="1em" />}
          Salvar
        </button>
      </ModalFooter>
    </Modal>
  );
}

function DeleteModal({ group }: { group: ClassRow }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await api.deleteClass(group.id);
      deleting.value = null;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="Excluir turma" icon={UsersRound} onClose={() => (deleting.value = null)}>
      <p class="text-sm text-muted-foreground">
        Vai apagar <b class="text-foreground">{group.name}</b>. Membros não são afetados (continuam como
        users); grants já materializados também ficam.
      </p>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (deleting.value = null)}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={go}
          disabled={busy}
          class="inline-flex items-center gap-1.5 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-500/15"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 width="1em" height="1em" />}
          Excluir
        </button>
      </ModalFooter>
    </Modal>
  );
}

function Modal({
  title,
  icon: Icon,
  onClose,
  children
}: {
  title: string;
  icon: typeof Users;
  onClose: () => void;
  children: any;
}) {
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Icon width="1.2em" height="1.2em" className="text-primary" />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
function ModalFooter({ children }: { children: any }) {
  return (
    <div class="sticky bottom-0 -mx-4 -mb-4 mt-2 flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
      {children}
    </div>
  );
}
